<template>
  <div class="card">
    <div class="card-header">Documents</div>
    <div class="card-body">
      <form-summary :validator="$v" ref="errorContainer" id="errorContainer"/>
      <b-row class="mb-3">
        <b-col>
          <p>
            Please upload all supporting documents required by law to process your order.
          </p>
        </b-col>
      </b-row>
      <b-row cols="12" v-if="requiredAttachmentOptions && requiredAttachmentOptions.length > 0">
        <b-col cols="12" md="4" lg="2" style="margin-bottom:8px;">
          <FileAttachment
                  v-on:attachment-added="handleUploadSuccess"
                  v-on:attachment-failed="handleUploadFailure"
                  :attachmentTypeOptions="requiredAttachmentOptions"
                  :order="orderNumber"
                  describedBy="attachments"
                  textField="option.label"
                  valueField="option"
                  supplementalField="relationship"
          />
        </b-col>
        <b-col cols="12" md="8" lg="10" id="othAttach">
          <strong>The following documents are REQUIRED:</strong>
          <p v-for="(item, index) in requiredAttachmentOptions" v-bind:key="index"
             style="margin-left:11px; margin-top:8px;">
            <span v-if="hasAttachment(item)">
                <b-icon icon="check2-square" scale="1.75" variant="success" aria-label="Valid"
                        alt="Valid" role="img"></b-icon>
            </span>
            <span v-else>
                <b-icon icon="x-circle" scale="1.75" variant="danger" aria-label="Invalid"
                        alt="Invalid" role="img"></b-icon>
            </span>
            <span style="margin-left:12px;">
              {{item.option.label}}
            </span>

          </p>
        </b-col>
      </b-row>
      <b-row cols="12" v-if="requiresOneAttachmentOptions && requiresOneAttachmentOptions.length > 0">
        <b-col cols="12" md="4" lg="2" style="margin-bottom:8px;">
          <FileAttachment
              v-on:attachment-added="handleUploadSuccess"
              v-on:attachment-failed="handleUploadFailure"
              :attachmentTypeOptions="requiresOneAttachmentOptions"
              :order="orderNumber"
              describedBy="attachments"
              textField="option.label"
              valueField="option"
              supplementalField="relationship"
          />
        </b-col>
        <b-col cols="12" md="8" lg="10" id="othAttach">
          <strong>At least one of the following documents are REQUIRED:</strong>
          <p v-for="(item, index) in requiresOneAttachmentOptions" v-bind:key="index"
             style="margin-left:11px; margin-top:8px;">
            <span v-if="hasAttachment(item)">
                <b-icon icon="check2-square" scale="1.75" variant="success" aria-label="Valid"
                        alt="Valid" role="img"></b-icon>
            </span>
            <span v-else>
                <b-icon icon="x-circle" scale="1.75" variant="danger" aria-label="Invalid"
                        alt="Invalid" role="img"></b-icon>
            </span>
            <span style="margin-left:12px;">
              {{item.option.label}}
            </span>

          </p>
        </b-col>
      </b-row>
      <b-row v-if="optionalAttachmentOptions.length > 0">
        <b-col cols="12" md="4" lg="2" style="margin-bottom:8px;">
          <FileAttachment
                  v-on:attachment-added="handleUploadSuccess"
                  v-on:attachment-failed="handleUploadFailure"
                  :attachmentTypeOptions="optionalAttachmentOptions"
                  :order="orderNumber"
                  describedBy="optionalAttachments"
                  textField="description"
                  valueField="code"
                  id="docs"
          />
        </b-col>
        <b-col cols="12" md="8" lg="10" id="optionalAttach">
          <strong>The following other attachments are OPTIONAL:</strong>
          <p v-for="(item, index) in optionalAttachmentOptions" v-bind:key="index"
             style="margin-left:11px; margin-top:8px;">
              <span v-if="hasAttachment(item.code)">
                  <b-icon icon="check2-square" scale="1.75" variant="success" aria-label="Valid"
                          alt="Valid" role="img"></b-icon>
              </span>
            <span v-else>
                <b-icon icon="question-diamond" scale="1.75" variant="warning" aria-label="Optional"
                        alt="Optional" role="img"></b-icon>
            </span>
            <span style="margin-left:12px;">{{item.description}}</span>
          </p>
        </b-col>
      </b-row>
      <b-row v-if="hasRelationship('AUTH_REP')" class="alert-danger mb-2 p-2">
        <b-col>
          <span class="font-weight-bold">*Signed Statements must include:</span>
          <ul class="list-disc mb-0">
            <li>Full name of the subject of the record</li>
            <li>Eligible person's (signer's) name and relationship to the subject of the record</li>
            <li>Authorized Representative's name</li>
            <li>Statement giving the Authorized Representative permission to obtain the record</li>
            <li>Eligible person's signature</li>
          </ul>
        </b-col>
      </b-row>
      <b-row v-if="hasHomeless" class="alert-info mb-2 p-2">
        <b-col>
          <span class="font-weight-bold">You must provide ONE of the following identifying documents:</span>
          <ul class="list-disc mb-0">
            <li>Copy of an unexpired ID that shows your picture or physical description, your signature, and the name of the agency that issued the ID to you.</li>
            <li>Affidavit of Homeless Status signed by an advocate and a copy of the advocate’s ID: form can be found at
              <a href="https://www.health.state.mn.us/people/vitalrecords/docs/homelessyouthbc.pdf" target="_blank">Homeless Youth Birth Certificate Request (opens in new tab)</a>
            </li>
            <li>
              Statement to identify–this must be notarized–form can be found at
              <a href="https://www.health.state.mn.us/people/vitalrecords/localreg/docs/statementid.pdf" target="_blank">Statement to Identify (opens in new tab)</a>
            </li>
          </ul>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <AttachmentList
              :value="attachments"
              :hasRelationship="true"
              v-on:attachment-removed="handleRemoveSuccess"
              modalId="documents"
          />
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>

  import FileAttachment from "@/components/templates/input/FileAttachment.vue";
  import AttachmentList from '@/components/templates/display/AttachmentList.vue';
  import LocalStorageService from "@/common/LocalStorageService";
  import {toastService} from "@/mixins/toastService"
  import axios from "axios"
  import axiosRetry from "axios-retry"

  axiosRetry(axios, {retries: 3, retryDelay: axiosRetry.exponentialDelay})
  const localStorageService = LocalStorageService.getService();

  export default {
    name: "Documents",
    components:{
      FileAttachment,
      AttachmentList,
    },
    mixins: [
      toastService
    ],
    data(){
      return {
        opts: {
          attachmentOptions: [],
        },
        orderNumber: null,
        attachments: [],
      }
    },
    props: {
      order: [Object],
      requiredAttachmentOptions: [Array],
      optionalAttachmentOptions: [Array],
      requiresOneAttachmentOptions: [Array],
    },
    validations(){
      return {
        haveAllAttachments: {
          reqAttach(){
            return this.haveAllAttachments();
          }
        }
      }
    },
    methods:{
      handleUploadSuccess(value) {
        toastService.showRootToast('File Uploaded', 'The file has been successfully uploaded to this application', 'success', this.$root);
        this.attachments.push(value)
      },
      handleUploadFailure(){
        toastService.showRootToast('Upload Failed', 'File Upload Failed','danger', this.$root);
      },
      handleRemoveSuccess(value) {
        let index = this.attachments.findIndex(i => i.attachmentId === value)
        this.attachments.splice(index, 1);
      },
      hasAttachment(item){
        return this.attachments.find(a => a.type.code === item.type.code &&
            ((a.relationship !== null && a.relationship.code === item.relationship.code)
                || (a.relationship === null && item.relationship === null)))
      },
      hasRelationship(item){
        return this.requiredAttachmentOptions.find(a => a.relationship !== null && a.relationship.code === item)
      },
      haveAllAttachments(){
        let haveAll = false;
        let hasReqs = this.requiredAttachmentOptions.length === 0 ||
            this.requiredAttachmentOptions.every(req => this.attachments
                .filter(a => req.type.code === a.type.code &&
                    ((req.relationship !== null && req.relationship.code === a.relationship.code)
                        || (req.relationship === null &&  a.relationship === null))).length > 0);
        let hasRequiresOneReqs = this.requiresOneAttachmentOptions.length === 0 ||
            this.requiresOneAttachmentOptions.some(req => this.attachments
              .filter(a => req.type.code === a.type.code && (req.relationship !== null && req.relationship.code === a.relationship.code).length > 0))

        if((this.requiredAttachmentOptions.length === 0 && this.requiresOneAttachmentOptions.length === 0)
            || (this.attachments.length > 0 && hasReqs && hasRequiresOneReqs)) {
          haveAll = true;
        }
        return haveAll;
      },
      validate() {
        this.$v.haveAllAttachments.$touch();
        let isValid = !this.$v.haveAllAttachments.$invalid;
        this.$emit('on-validate', this.$data, isValid);
        return isValid;
      },
      loadCurrentAttachments() {
        axios.get(`${process.env.VUE_APP_API_BASE_URL}/attachments/current/${this.orderNumber}`)
                .then(response => {
                  this.attachments = response.data
                })
      }
    },
    computed: {
      hasHomeless() {
        return this.order?.orderProducts?.length > 0 && this.order?.orderProducts.filter(p => p.product.code === "BIRTH_HOMELESS").length > 0
      }
    },
    created() {
      this.orderNumber = localStorageService.getOrderNumber()
    },
    mounted() {
      this.loadCurrentAttachments();
    },
  }
</script>

<style scoped>

</style>
